import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router'
import invariant from 'tiny-invariant'

import { SessionItemUser } from '../../types/responses/sessions'
import { isDevelopment, isProduction } from '../helpers/envHelpers'
import { slugify } from '../helpers/miscHelpers'
import { errorTypeService } from './errorTypeService'

class SentryService {
  init() {
    const apiUrl = import.meta.env.VITE_API_URL
    invariant(apiUrl, 'Failed to determine API URL')

    const dsn = import.meta.env.VITE_SENTRY_DSN || ''

    Sentry.init({
      enabled: !!dsn && import.meta.env.VITE_ENABLE_SENTRY === 'true',
      normalizeDepth: 7,
      dsn,
      environment: this.getEnvironment(),
      sendDefaultPii: true,

      // Only attach Sentry headers to outgoing XHR requests to the API
      // https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/#tracepropagationtargets
      tracePropagationTargets: [apiUrl],

      ignoreErrors: [
        // This error surfaces in Safari and appears to come from Posthog.
        // We need to investigate further but as it doesn't seem to affect the
        // user experience, let's filter it out to avoid depleting our quota.
        "undefined is not an object (evaluating 'o[Symbol.iterator]')",
      ],

      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],

      tracesSampleRate: 0.2,
    })
  }

  getEnvironment() {
    switch (true) {
      case isDevelopment():
        return 'development'
      case isProduction():
        return 'production'
      default:
        return 'unknown'
    }
  }

  isEnabled() {
    return !!import.meta.env.VITE_SENTRY_DSN
  }

  captureApiError(error: unknown) {
    Sentry.setExtra('error', error)

    const isAxiosError = errorTypeService.isAxiosError(error)

    if (isAxiosError) {
      Sentry.setExtra('error.request', error.request)
      Sentry.setExtra('error.response', error.response)
    }

    if (error instanceof Error) {
      // Sentry's captureException() method requires an instance of Error
      // otherwise it'll throw an error.
      Sentry.captureException(error)
    }

    const statusCode = isAxiosError ? error.response?.status : undefined
    const errorMessage = statusCode ? `API error: ${statusCode}` : 'API error'

    sentryService.captureMessage({
      messageId: 'api-error',
      message: errorMessage,
      extra: { error },
    })
  }

  setAppContext(context: SentryAppContext) {
    const { currentUser } = context

    if (!currentUser) {
      return
    }

    const appContext = { currentUser }

    Sentry.setContext('App Context', appContext)
  }

  captureMessage(options: {
    message: string
    messageId?: string
    extra?: SentryExtraContext
  }) {
    const { message, messageId, extra } = options

    Sentry.withScope((scope) => {
      if (extra) {
        scope.setExtra('extra', extra)
      }

      Sentry.captureMessage(message, {
        tags: { 'app.messageId': messageId || slugify(message) },
        extra,
      })
    })
  }
}

export type SentryExtraContext = Record<string, unknown>

export interface SentryAppContext {
  currentUser: SessionItemUser
}

const sentryService = new SentryService()

export default sentryService
